import './index.css';
import { IoPlayOutline, IoPauseOutline, IoChevronUpOutline, IoChevronDownOutline, IoRadio, IoClose } from "react-icons/io5";
import { useState, useEffect, useRef } from 'react';
import PulseAnimation from '../PulseAnimation';
import Loader from '../Loader';

/*
    COMPONENTE PLAYER - Reproductor de Radio
 
    Funcionalidades principales:
    - Reproduce múltiples radios desde API
    - Control de play/pause con estados de carga
    - Minimizar/maximizar reproductor
    - Integración con MediaSession (controles móvil)
    - Lista de radios disponibles
  
    Estados importantes:
    - loadTrack: controla cuándo mostrar loader vs botones play/pause
    - player: true = reproduciendo, false = pausado
    - radios: array de radios desde API
    - posAudioPlayer: true = minimizado, false = maximizado
    
 */
export default function Player({ setAudioPlayerOn }) {
    // ESTADOS DEL REPRODUCTOR
    const [timeLine, setTimeLine] = useState(0); // avance línea de tiempo de audio actual, representada en una barra div
    const [posAudioPlayer, setPosAudioPlayer] = useState(false); // posición del reproductor, minimizado o maximizado
    const [player, setPlayer] = useState(false); // true = reproduciendo, false = pausado
    const [loadTrack, setLoadTrack] = useState(false); // true = audio listo, false = cargando (muestra loader)
    const [currentTimePlayer, setCurrentTimePlayer] = useState('00:00'); // tiempo actual de reproducción formateado parte mostrando 00:00
    const audioPlayer = useRef(null); // referencia al elemento audio HTML
    const timeLineComp = useRef(null); // referencia al elemento timeline para modificar el currentTime del currentAudio

    // ESTADOS DE LA INFORMACIÓN DEL AUDIO ACTUAL
    const [audioSrc, setAudioSrc] = useState(); // URL del stream de audio
    const [audioInfo, setAudioInfo] = useState(); // nombre del artista/radio
    const [albumInfo, setAlbumInfo] = useState(); // información del álbum
    const [titleInfo, setTitleInfo] = useState(); // título de la canción
    const [coverUrl, setCoverUrl] = useState(); // URL de la imagen de portada

    // ESTADOS DE LA LISTA DE RADIOS
    const [radios, setRadios] = useState(null); // array de radios desde API (null inicial)
    const [loading, setLoading] = useState(true); // estado de carga de la API

    // EFECTO: CARGAR RADIOS DESDE API
    useEffect(() => {
        const apiUrl = `/api/${localStorage.getItem('playlist')}`;
        const fetchData = async () => {
            try {
                const response = await fetch(apiUrl);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const jsonData = await response.json();
                setRadios(jsonData);
                setLoading(false);
            } catch (e) {
                console.error('Error cargando radios:', e);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // EFECTO: CONFIGURAR PRIMERA RADIO CUANDO SE CARGAN LOS DATOS
    useEffect(() => {
        if (radios && radios.length > 0) {
            setLoadTrack(false);
            setPlayer(false); // NUEVO: Asegurar que inicie en pausado

            if (localStorage.getItem('currentAudio')) {
                setAudioSrc(radios[localStorage.getItem('currentAudio')].audio_src);
                setAudioInfo(radios[localStorage.getItem('currentAudio')].audio_info);
                setAlbumInfo(radios[localStorage.getItem('currentAudio')].album_info);
                setTitleInfo(radios[localStorage.getItem('currentAudio')].title_info);
                setCoverUrl(radios[localStorage.getItem('currentAudio')].cover_url);

                // Restaurar tiempo después de que se cargue el audio
                setTimeout(() => {
                    const savedTime = localStorage.getItem('currentTime');
                    if (savedTime && audioPlayer.current) {
                        const duration = audioPlayer.current.duration;
                        if (duration !== Infinity && !isNaN(duration) && duration > 0) {
                            audioPlayer.current.currentTime = parseFloat(savedTime);
                        }
                    }
                }, 500); // Dar tiempo para que cargue
            } else {
                setAudioSrc(radios[0].audio_src);
                setAudioInfo(radios[0].audio_info);
                setAlbumInfo(radios[0].album_info);
                setTitleInfo(radios[0].title_info);
                setCoverUrl(radios[0].cover_url);
                localStorage.setItem('currentAudio', 0); // Monta el estado del current audio para restaurar el reproductor en caso de refrescar la página
            }
            // Esperar a que el audio esté listo antes de permitir controles
            const handleCanPlay = () => {
                setLoadTrack(true);
                audioPlayer.current.removeEventListener('canplay', handleCanPlay);
            };

            if (audioPlayer.current) {
                audioPlayer.current.addEventListener('canplay', handleCanPlay);
                audioPlayer.current.load();
            }
        }
    }, [radios]);

    // EFECTO: CONFIGURAR EVENT LISTENERS DEL AUDIO AL CARGAR EL SCRIPT
    useEffect(() => {
        if (audioPlayer.current) {
            audioPlayer.current.addEventListener('timeupdate', addTimeUpdateEvent);
            audioPlayer.current.addEventListener('canplay', addCanPlayEvent);
        }
        // Cleanup: remover listeners al desmontar componente
        return () => {
            if (audioPlayer.current) {
                audioPlayer.current.removeEventListener('timeupdate', addTimeUpdateEvent);
                audioPlayer.current.removeEventListener('canplay', addCanPlayEvent);
            }
        };
    }, []);

    /** OBTIENE DONDE SE HIZO CLICK EN EL ELEMENTO BAJO EL TIMELINE */
    const handleClickTimeLine = (event) => {
        if (timeLineComp.current) {
            // Obtener el rectángulo delimitador del div para sus dimensiones y posición.
            const rect = timeLineComp.current.getBoundingClientRect();

            // Obtener la coordenada X global del clic (respecto al viewport).
            const clientX = event.clientX;

            // Calcular la coordenada X relativa al div.
            const xRelativeToDiv = clientX - rect.left;

            // Calcular el porcentaje y redondearlo hacia abajo.
            const percentageX = Math.floor((xRelativeToDiv / rect.width) * 100);

            // Mostrar solo el porcentaje.
            setTimeLine(percentageX + '%');
            if (audioPlayer.current.duration !== Infinity) {
                audioPlayer.current.currentTime = (percentageX / 100) * audioPlayer.current.duration;
            }
        }
    };

    //** AGREGA EVENTO CLICK PARA EL ELEMENTO QUE ESTÁ COMO BASE DE LA LINEA DE TIEMPO, PARA PODER HACER CLICK Y 
    // OBTENER EL LUGAR DEL CLICK Y PODER CAMBIAR EL currentTime */
    useEffect(() => {
        if (timeLineComp.current) {
            timeLineComp.current.addEventListener('click', handleClickTimeLine);
        }
        return () => {
            if (timeLineComp.current) {
                timeLineComp.current.removeEventListener('click', handleClickTimeLine);
            }
        };
    }, []);
    // INTEGRACIÓN CON MEDIASESSION (CONTROLES MÓVILES)
    if (coverUrl) {
        const currentInfoAudio = {
            title: titleInfo,
            artist: audioInfo,
            album: albumInfo,
            artwork: [
                { src: coverUrl, sizes: '256x256', type: 'image/png' },
            ]
        };

        if ('mediaSession' in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: currentInfoAudio.title,
                album: currentInfoAudio.album,
                artist: currentInfoAudio.album,
                artwork: currentInfoAudio.artwork
            });
            // Configurar handlers para controles móviles
            navigator.mediaSession.setActionHandler('play', () => {
                playerOn();
            });
            navigator.mediaSession.setActionHandler('pause', () => {
                playerOff();
            });
        }
    }

    // FUNCIONES UTILITARIAS

    // Formatea segundos a formato MM:SS o HH:MM:SS
    const formatTime = (seconds) => {
        if (seconds == 'Infinity') {
            return '--:--';
        }
        if (isNaN(seconds) || seconds < 0) {
            return '00:00';
        }
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        const pad = (num) => String(num).padStart(2, '0');

        if (hours > 0) {
            return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
        } else {
            return `${pad(minutes)}:${pad(remainingSeconds)}`;
        }
    };

    // EVENT HANDLERS DEL AUDIO

    // Se ejecuta cada vez que cambia el tiempo de reproducción
    // Actualiza el display del tiempo actual
    const addTimeUpdateEvent = () => {
        if (audioPlayer.current) {
            setCurrentTimePlayer(formatTime(audioPlayer.current.currentTime));
        }
    }

    // Se ejecuta cuando el audio está listo para reproducir
    // Oculta el loader y muestra los botones de control
    const addCanPlayEvent = () => {
        if (audioPlayer.current) {
            setLoadTrack(true); // Audio listo, mostrar botones
        }
    }

    // FUNCIONES DE CONTROL DEL REPRODUCTOR

    // Inicia la reproducción
    const playerOn = () => {
        setPlayer(true);
        if (audioPlayer.current) {
            audioPlayer.current.play();
        }
    }

    // Pausa la reproducción
    const playerOff = () => {
        setPlayer(false);
        if (audioPlayer.current) {
            audioPlayer.current.pause();
        }
    }

    // Cambia a una radio diferente de la lista
    // IMPORTANTE: Resetea loadTrack para mostrar loader mientras carga
    function changeRadio(index) {
        setLoadTrack(false); // CRÍTICO: Mostrar loader mientras carga nueva radio. Evita interacción con controles play y pause
        setAudioSrc(radios[index].audio_src);
        setAudioInfo(radios[index].audio_info);
        setAlbumInfo(radios[index].album_info);
        setTitleInfo(radios[index].title_info);
        setCoverUrl(radios[index].cover_url);

        localStorage.setItem('currentTime', 0); // Reinicio el estado del currenTime y la timeline
        localStorage.setItem('currentAudio', index); // Cambia el estado para restaurar si es necesario el current audio

        audioPlayer.current.load();

        // Comprueba que al cambio de audio conserve el estado (pause o play)
        if (player) {
            setTimeLine(0); // Pone en cero la línea de tiempo al momento de cambiar el audio
            playerOn();
        }
    }

    // FUNCIONES DE UI

    // Minimiza/Maximiza el reproductor
    const togglePosAudioPlayer = () => {
        setPosAudioPlayer(!posAudioPlayer);
    }
    // Pausa, cierra y 
    // destruye la variable player en localStorage (conservaba el estado del reproductor 
    // abierto como lo dejó al salir de la web)
    const closeAudioPlayer = () => {
        playerOff();
        localStorage.removeItem('currentTime'); // Elimina el estado del currentTime del audio actual
        localStorage.removeItem('player'); // Elimina el estado de player que indica si está abierto o cerrado el audioplayer
        localStorage.removeItem('playlist'); // Elimina el estado de la playlist que guarda la playlist activa
        localStorage.removeItem('currentAudio'); // Elimina el estado guardado del current audio que conserva el audio en reproducción
        setAudioPlayerOn(false);
    };

    /** Actualiza el estado de la línea de tiempo, cada que cambia el currenTimePlayer */
    useEffect(() => {
        if (audioPlayer.current) {
            if (audioPlayer.current.duration) {
                setTimeLine(`${((audioPlayer.current.currentTime / audioPlayer.current.duration) * 100).toFixed(1)}%`); // Calcula el porcentaje para mostrar la timeline
                localStorage.setItem('currentTime', audioPlayer.current.currentTime); // Actualiza el estado de la var en el localStorage para restaurar si es necesario
            }
        }
    }, [currentTimePlayer]);

    // RENDER
    return (
        <section className={`${posAudioPlayer ? 'player lower-audio-player' : 'player upper-audio-player'}`}>
            <div className="player-content">
                {/* CONTROLES PRINCIPALES */}
                <div className="top-player">
                    {/* Animación de pulso cuando reproduce */}
                    <div>
                        <PulseAnimation state={player} />
                    </div>
                    {/* Botones play/pause con loader condicional */}
                    <div className="controls">
                        {loadTrack ? (
                            <div>
                                <div className={player ? 'hidden' : 'pause'}>
                                    <IoPlayOutline className="icon-controls" onClick={playerOn} />
                                </div>
                                <div className={player ? 'pause' : 'hidden'}>
                                    <IoPauseOutline className="icon-controls" onClick={playerOff} />
                                </div>
                            </div>
                        ) : (
                            <Loader topAvance={15} /> // Mostrar loader mientras carga audio
                        )}
                    </div>
                    {/* Botón minimizar/maximizar */}
                    <div className="up-player">
                        {posAudioPlayer ?
                            <IoChevronUpOutline className="icon-up" onClick={togglePosAudioPlayer} /> :
                            <IoChevronDownOutline className="icon-up" onClick={togglePosAudioPlayer} />
                        }
                    </div>
                </div>

                {/* INFORMACIÓN DE LA CANCIÓN ACTUAL * */}
                <div className="info-track">
                    <div className="text-info-track">
                        <span>{titleInfo}</span>
                    </div>
                </div>

                {/* ELEMENTO AUDIO HTML (OCULTO) */}
                <div className="audio-player">
                    <audio controls preload="auto" ref={audioPlayer}>
                        <source src={audioSrc} type="audio/mpeg" />
                        Tu navegador no soporta la reproducción de audio.
                    </audio>
                </div>

                {/** Display que muestra la línea de tiempo de audio actual */}
                <div className="current-time-display">
                    {/* Tiempo actual de reproducción */}
                    <div className="current-time">
                        {currentTimePlayer}
                    </div>
                    {/** línea de avance del tiempo del audio actual */}
                    <div className="wide-line">
                        <div className="base-time-line" ref={timeLineComp}>
                            <div className="time-line" style={{ width: timeLine }}></div>
                        </div>
                    </div>
                    {/* Duración del audio si está disponible */}
                    <div className="current-duration">
                        {/* Muestra la duración del audio si es un audio grabado, si es infinity muestra --:-- */}
                        {audioPlayer.current && formatTime(audioPlayer.current.duration)}
                    </div>
                </div>
            </div>

            {/* LISTA DE RADIOS DISPONIBLES */}
            <div className="file-list">
                {/* Cierra el audioplayer cambiando el estado de audioPlayerOn a false (estado que viene desde App.js por props)*/}
                <div className="close-audio-player">
                    <IoClose className="icon-close" onClick={closeAudioPlayer} />
                </div>
                <div className="playlist" >
                    {loading ? (
                        <div className="loader-position">
                            <Loader /> {/* Mostrar loader mientras cargan las radios */}
                        </div>
                    ) : (
                        radios && radios.map((radio, index) => (
                            <div className="audio-playlist" key={index}>
                                <IoRadio />
                                <span onClick={() => changeRadio(index)}>
                                    {radio.audio_info}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </section>
    );
}